import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPinIcon, 
  StarIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  GlobeAltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface SellerDetail {
  seller_id: number;
  business_name: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  business_type: string;
  profile_type: string;
  verification_status: string;
  rating: number;
  review_count: number;
  description: string;
  specialties: string[];
  inventory_status: string;
  operating_since: string;
  website_url?: string;
  social_media?: {
    facebook?: string;
    instagram?: string;
  };
  products: Array<{
    id: number;
    name: string;
    category: string;
    price: string;
    availability: string;
    description: string;
  }>;
  reviews: Array<{
    id: number;
    reviewer_name: string;
    rating: number;
    comment: string;
    date: string;
  }>;
  hours: {
    [key: string]: string;
  };
}

const SellerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [seller, setSeller] = useState<SellerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'reviews' | 'contact'>('overview');

  useEffect(() => {
    if (!id) return;

    const fetchSellerDetail = async () => {
      try {
        const response = await fetch(`/api/sellers/${id}`);
        if (!response.ok) {
          throw new Error('Seller not found');
        }
        const data = await response.json();
        setSeller(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load seller details');
      } finally {
        setLoading(false);
      }
    };

    fetchSellerDetail();
  }, [id]);

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <div key={star}>
            {star <= rating ? (
              <StarIconSolid className="w-5 h-5 text-yellow-400" />
            ) : (
              <StarIcon className="w-5 h-5 text-gray-300" />
            )}
          </div>
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating.toFixed(1)})</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading seller details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !seller) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error || 'Seller not found'}</p>
            <Link 
              to="/directory"
              className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
            >
              Back to Directory
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex mb-6 text-sm">
          <Link to="/" className="text-amber-600 hover:text-amber-800">Home</Link>
          <span className="mx-2 text-gray-500">/</span>
          <Link to="/directory" className="text-amber-600 hover:text-amber-800">Directory</Link>
          <span className="mx-2 text-gray-500">/</span>
          <span className="text-gray-500">{seller.business_name}</span>
        </nav>

        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="md:flex">
            {/* Business Image */}
            <div className="md:w-1/3 h-64 md:h-80 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
              <span className="text-6xl">🐝</span>
            </div>

            {/* Business Info */}
            <div className="md:w-2/3 p-6 md:p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{seller.business_name}</h1>
                  <p className="text-lg text-gray-600 mb-2">{seller.contact_name}</p>
                  
                  <div className="flex items-center text-gray-600 mb-3">
                    <MapPinIcon className="w-5 h-5 mr-2" />
                    <span>{seller.city}, {seller.state}</span>
                  </div>

                  <div className="mb-4">
                    {renderStars(seller.rating)}
                    <p className="text-sm text-gray-500 mt-1">
                      Based on {seller.review_count} review{seller.review_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-2">
                  {seller.profile_type === 'premium' && (
                    <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                      PREMIUM
                    </span>
                  )}
                  
                  {seller.verification_status === 'verified' && (
                    <div className="flex items-center text-green-600 text-sm">
                      <ShieldCheckIcon className="w-4 h-4 mr-1" />
                      <span>Verified</span>
                    </div>
                  )}

                  <span className={`px-3 py-1 rounded-full text-sm ${
                    seller.inventory_status === 'in_stock' ? 'bg-green-100 text-green-800' :
                    seller.inventory_status === 'low_stock' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {seller.inventory_status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Business Type and Specialties */}
              <div className="mb-4">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mr-3">
                  {seller.business_type}
                </span>
                <span className="text-gray-600 text-sm">
                  Operating since {seller.operating_since}
                </span>
              </div>

              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Specialties:</p>
                <div className="flex flex-wrap gap-2">
                  {seller.specialties.map((specialty, index) => (
                    <span 
                      key={index}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed">{seller.description}</p>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'products', label: 'Products' },
                { key: 'reviews', label: 'Reviews' },
                { key: 'contact', label: 'Contact' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4">About This Business</h3>
                  <p className="text-gray-700 mb-6">{seller.description}</p>
                  
                  <h4 className="font-medium mb-3">Business Hours</h4>
                  <div className="space-y-2">
                    {Object.entries(seller.hours).map(([day, hours]) => (
                      <div key={day} className="flex justify-between">
                        <span className="capitalize text-gray-600">{day}:</span>
                        <span className="text-gray-900">{hours}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Location & Map</h3>
                  {/* Map Integration - You can integrate with Azure Maps here */}
                  <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center mb-4">
                    <div className="text-center text-gray-600">
                      <MapPinIcon className="w-12 h-12 mx-auto mb-2" />
                      <p>Interactive Map</p>
                      <p className="text-sm">Azure Maps Integration</p>
                    </div>
                  </div>
                  <p className="text-gray-700">{seller.address}</p>
                </div>
              </div>
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
              <div>
                <h3 className="text-lg font-semibold mb-6">Available Products</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {seller.products.map((product) => (
                    <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{product.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          product.availability === 'available' ? 'bg-green-100 text-green-800' :
                          product.availability === 'limited' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {product.availability}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{product.category}</p>
                      <p className="text-gray-700 text-sm mb-3">{product.description}</p>
                      <p className="font-semibold text-amber-600">{product.price}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">Customer Reviews</h3>
                  <button className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors">
                    Write a Review
                  </button>
                </div>

                <div className="space-y-6">
                  {seller.reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-200 pb-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{review.reviewer_name}</h4>
                          <div className="flex items-center mt-1">
                            {renderStars(review.rating)}
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">{review.date}</span>
                      </div>
                      <p className="text-gray-700">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === 'contact' && (
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-6">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <PhoneIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <a href={`tel:${seller.phone}`} className="text-amber-600 hover:text-amber-800">
                        {seller.phone}
                      </a>
                    </div>
                    
                    <div className="flex items-center">
                      <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <a href={`mailto:${seller.email}`} className="text-amber-600 hover:text-amber-800">
                        {seller.email}
                      </a>
                    </div>
                    
                    {seller.website_url && (
                      <div className="flex items-center">
                        <GlobeAltIcon className="w-5 h-5 text-gray-400 mr-3" />
                        <a 
                          href={seller.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-amber-600 hover:text-amber-800"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <MapPinIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-gray-700">{seller.address}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-6">Get Directions</h3>
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p className="text-gray-700 mb-4">
                      Click below to get directions to {seller.business_name}
                    </p>
                    <button className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors">
                      Open in Maps
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerDetailPage;